<div class="ai-review-shell">
  <!-- Loading & error states ------------------------------------------------>
  <div class="ai-loading" *ngIf="loading">
    <div class="spinner"></div>
    <p>Loading…</p>
  </div>

  <div class="ai-error" *ngIf="!loading && loadError">
    <p>We couldn't load the review experience. Please try again.</p>
  </div>

  <!-- Main flow ----------------------------------------------------------- -->
  <div class="ai-review-card" *ngIf="!loading && !loadError && config">

    <!-- Progress bar (top of card, 4 segment dots, no percentage) ------- -->
    <div
      class="progress-bar"
      *ngIf="!isShowThankYouScreen"
      role="progressbar"
      [attr.aria-valuemin]="1"
      [attr.aria-valuemax]="4"
      [attr.aria-valuenow]="step"
      aria-label="Review progress"
    >
      <div class="progress-track">
        <div class="progress-fill" [style.width.%]="(step / 4) * 100"></div>
      </div>
      <div class="progress-dots">
        <span
          *ngFor="let i of [1, 2, 3, 4]"
          class="progress-dot"
          [class.reached]="step >= i"
          [class.active]="step === i"
        ></span>
      </div>
    </div>

    <!-- Top bar: back chevron only (hidden on terminal thank-you) -------- -->
    <header class="ai-topbar" *ngIf="!isShowThankYouScreen">
      <button
        type="button"
        class="back-btn"
        *ngIf="step > 1"
        (click)="goBack()"
        aria-label="Back"
      >
        &larr;
      </button>
      <span class="topbar-spacer" aria-hidden="true"></span>
      <span class="topbar-spacer" aria-hidden="true"></span>
    </header>

    <!-- Business hero (hidden on terminal thank-you) --------------------- -->
    <section class="business-hero" *ngIf="!isShowThankYouScreen" [class.compact]="step > 1">
      <img
        *ngIf="config.business_logo"
        [src]="config.business_logo"
        [alt]="config.business_name"
        class="business-logo"
      />
      <h1 class="business-title">{{ config.business_name | titlecase }}</h1>
      <p class="business-greeting" *ngIf="config.contact_name">
        Hello {{ config.contact_name }}!
      </p>
      <p class="business-welcome" *ngIf="welcomeMessage || config.consumer_welcome_message">
        {{ welcomeMessage || config.consumer_welcome_message }}
      </p>
    </section>

    <!-- Thank-you screen — variant depends on sentiment / already-given -->
    <section
      class="screen thank-you-screen"
      *ngIf="isShowThankYouScreen"
      [class.is-negative]="sentiment === 'negative' && !isFeedbackAlreadyGiven"
    >
      <div class="thank-you-illustration">
        <ng-container
          *ngIf="sentiment === 'negative' && !isFeedbackAlreadyGiven; else positiveIcon"
        >
          <div class="apology-mark" aria-hidden="true">
            <svg viewBox="0 0 64 64" width="72" height="72">
              <circle cx="32" cy="32" r="30" fill="#fdecea" />
              <path
                d="M32 18v18"
                stroke="#e85a4f"
                stroke-width="4"
                stroke-linecap="round"
              />
              <circle cx="32" cy="44" r="3" fill="#e85a4f" />
            </svg>
          </div>
        </ng-container>
        <ng-template #positiveIcon>
          <img
            src="assets/images/thank-you-icon.svg"
            class="img-responsive"
            alt="Thank you"
          />
        </ng-template>
      </div>

      <h2 class="thank-you-heading">
        <ng-container
          *ngIf="sentiment === 'negative' && !isFeedbackAlreadyGiven; else positiveHeading"
        >
          We hear you
        </ng-container>
        <ng-template #positiveHeading>Thank you for your feedback</ng-template>
      </h2>

      <p class="thank-you-body">
        <ng-container *ngIf="isFeedbackAlreadyGiven; else freshThankYou">
          {{ feedbackAlreadyGivenMessage }}
        </ng-container>
        <ng-template #freshThankYou>
          <ng-container *ngIf="sentiment === 'negative'; else positiveBody">
            We are sorry for the bad experience. We are on it. Expect an update quickly.
          </ng-container>
          <ng-template #positiveBody>
            {{ thankYouMessage || 'We appreciate you taking the time to share your experience with us.' }}
          </ng-template>
        </ng-template>
      </p>

      <p
        class="thank-you-signoff"
        *ngIf="sentiment === 'negative' && !isFeedbackAlreadyGiven && config.business_name"
      >
        — The {{ config.business_name | titlecase }} team
      </p>
    </section>

    <!-- Animated step container ----------------------------------------- -->
    <div class="screen-stage" *ngIf="!isShowThankYouScreen" [@slideStep]="step">

      <!-- ====================== SCREEN 1 ====================== -->
      <section class="screen" *ngIf="step === 1">
        <h2 class="heading">How was your overall experience with us?</h2>
        <p class="subheading">Tap the option that best describes your visit</p>

        <div class="thumbs-row">
          <button
            class="thumb-btn positive"
            [class.selected]="sentiment === 'positive'"
            (click)="selectSentiment('positive')"
            aria-label="Positive"
          >
            <img src="assets/images/thumbs-up.svg" alt="" />
            <span>Good</span>
          </button>

          <button
            class="thumb-btn negative"
            [class.selected]="sentiment === 'negative'"
            (click)="selectSentiment('negative')"
            aria-label="Negative"
          >
            <img src="assets/images/thumbs-down.svg" alt="" />
            <span>Needs Improvement</span>
          </button>
        </div>
      </section>

      <!-- ====================== SCREEN 2 ====================== -->
      <section class="screen" *ngIf="step === 2">
        <h2 class="heading" *ngIf="!isKeywordSelectionLocked">Which parts of your visit would you like to mention?</h2>
        <p class="subheading" *ngIf="!isKeywordSelectionLocked">Select one or more topics — you can always add "Others"</p>

        <h2 class="heading" *ngIf="isKeywordSelectionLocked">Here's what we'll mention in your review</h2>
        <p class="subheading" *ngIf="isKeywordSelectionLocked">These topics were chosen for you</p>

        <div class="chip-grid">
          <button
            *ngFor="let kw of keywordLabels"
            type="button"
            class="chip"
            [class.selected]="isKeywordSelected(kw)"
            [class.locked]="isKeywordSelectionLocked"
            [class.others]="kw === config.others_keyword"
            [disabled]="isKeywordSelectionLocked"
            (click)="toggleKeyword(kw)"
          >
            {{ kw }}
          </button>
        </div>

        <div class="others-input" *ngIf="isOthersSelected">
          <label for="othersText">Tell us briefly:</label>
          <input
            id="othersText"
            type="text"
            maxlength="80"
            placeholder="e.g. parking was easy"
            [(ngModel)]="othersText"
            autocomplete="off"
          />
        </div>
      </section>

      <!-- ====================== SCREEN 3 ====================== -->
      <section class="screen" *ngIf="step === 3">
        <h2 class="heading">Choose up to 3 words that describe your experience</h2>
        <p class="subheading">These will shape how your review sounds</p>

        <div class="selected-keywords-strip">
          <span class="kw-pill" *ngFor="let kw of selectedKeywords">
            {{ kw === config.others_keyword && othersText ? othersText : kw }}
          </span>
        </div>

        <div class="chip-grid">
          <button
            *ngFor="let w of currentWordPool"
            type="button"
            class="chip word-chip"
            [class.selected]="isWordSelected(w)"
            (click)="toggleWord(w)"
          >
            {{ displayWord(w) }}
          </button>
        </div>
      </section>

      <!-- ====================== SCREEN 4 — negative feedback ====================== -->
      <section class="screen" *ngIf="step === 4 && sentiment === 'negative'">
        <h2 class="heading">Tell us what went wrong</h2>
        <p class="subheading">Your feedback helps us improve.</p>

        <!-- Subtle recap of selections from screens 2-3 -->
        <div class="selections-recap" *ngIf="selectionsRecap.length">
          <span class="selections-recap-label">Mentioning:</span>
          <span class="selections-recap-pill" *ngFor="let s of selectionsRecap">{{ s }}</span>
        </div>

        <div class="additional-editor">
          <textarea
            rows="6"
            maxlength="600"
            [(ngModel)]="feedbackText"
            placeholder="Share what happened — we'll take it from here."
            autofocus
          ></textarea>
        </div>
      </section>

      <!-- ====================== SCREEN 4 — positive draft ====================== -->
      <section class="screen" *ngIf="step === 4 && sentiment === 'positive'">
        <h2 class="heading">Here's your review — edit it before posting</h2>
        <p class="subheading">Use the buttons below to refine, shorten, or lengthen it</p>

        <!-- Subtle recap of selections from screens 2-3 -->
        <div class="selections-recap" *ngIf="selectionsRecap.length">
          <span class="selections-recap-label">Including:</span>
          <span class="selections-recap-pill" *ngFor="let s of selectionsRecap">{{ s }}</span>
        </div>

        <!-- Inline "Anything to add?" banner: Add / Edit / Save / Cancel -->
        <div class="additional-banner" *ngIf="!isEditingAdditional">
          <ng-container *ngIf="!additionalText.trim(); else hasAddedTpl">
            <span class="ab-prompt">Would you like to add anything?</span>
            <button
              type="button"
              class="ab-action"
              [disabled]="isGenerating || isRefining"
              (click)="openAdditionalEditor()"
            >
              + Add
            </button>
          </ng-container>
          <ng-template #hasAddedTpl>
            <span class="ab-label">Including:</span>
            <span class="ab-text">"{{ additionalText.trim() }}"</span>
            <button
              type="button"
              class="ab-action"
              [disabled]="isGenerating || isRefining"
              (click)="openAdditionalEditor()"
            >
              Edit
            </button>
          </ng-template>
        </div>

        <div class="additional-editor" *ngIf="isEditingAdditional">
          <textarea
            rows="2"
            maxlength="200"
            [(ngModel)]="additionalDraft"
            placeholder="A name or detail you want included"
            autofocus
          ></textarea>
          <div class="ae-actions">
            <button type="button" class="link-btn" (click)="cancelAdditionalEditor()">
              Cancel
            </button>
            <button type="button" class="save-btn" (click)="saveAdditionalEditor()">
              Save
            </button>
          </div>
        </div>

        <div class="draft-area" [class.dim]="isGenerating || isRefining">
          <textarea
            rows="6"
            maxlength="600"
            [(ngModel)]="reviewDraft"
            [disabled]="isGenerating"
            placeholder="Your review will appear here…"
          ></textarea>

          <div class="draft-overlay" *ngIf="isGenerating">
            <div class="spinner small"></div>
            <span>Generating…</span>
          </div>
        </div>

        <div class="refine-row">
          <button
            class="refine-btn"
            [disabled]="isGenerating || isRefining || !reviewDraft"
            (click)="refine('refine')"
          >
            Refine
          </button>
          <button
            class="refine-btn"
            [disabled]="isGenerating || isRefining || !reviewDraft"
            (click)="refine('short')"
          >
            Short
          </button>
          <button
            class="refine-btn"
            [disabled]="isGenerating || isRefining || !reviewDraft"
            (click)="refine('long')"
          >
            Long
          </button>
          <button
            class="refine-btn regenerate"
            [disabled]="isGenerating || isRefining"
            (click)="generate()"
            title="Generate a fresh draft"
            aria-label="Regenerate"
          >
            ↻
          </button>
        </div>
      </section>
    </div>

    <!-- Sticky bottom CTA — varies per step ----------------------------- -->
    <footer class="ai-footer" *ngIf="step !== 1 && !isShowThankYouScreen">
      <button
        *ngIf="step === 2"
        class="primary-btn"
        [disabled]="!canProceedFromScreen2"
        (click)="goNext()"
      >
        Continue
      </button>

      <button
        *ngIf="step === 3"
        class="primary-btn"
        [disabled]="selectedWords.length === 0"
        (click)="goNext()"
      >
        {{ sentiment === 'negative' ? 'Continue' : 'Generate Review' }}
      </button>

      <button
        *ngIf="step === 4 && sentiment === 'negative'"
        class="primary-btn"
        [disabled]="!feedbackText.trim() || submitInFlight"
        (click)="submit()"
      >
        Submit Feedback
      </button>

      <button
        *ngIf="step === 4 && sentiment === 'positive'"
        class="primary-btn"
        [disabled]="isGenerating || !reviewDraft.trim() || submitInFlight"
        (click)="submit()"
      >
        Copy &amp; Post on Google
      </button>
    </footer>

    <!-- Thank-you mockup (negative sentiment only, no API call) --------- -->
    <div class="thankyou-overlay" *ngIf="showThankYouModal" (click)="closeThankYou()">
      <div class="thankyou-dialog" (click)="$event.stopPropagation()">
        <button
          type="button"
          class="close close-icon-modal"
          aria-label="Close"
          (click)="closeThankYou()"
        >
          <span aria-hidden="true">&times;</span>
        </button>
        <div class="col-lg-12 col-md-12 col-sm-12 thank-you-sec" style="width: auto">
          <div class="thank-you-icon-sec">
            <img src="assets/images/thank-you-icon.svg" class="img-responsive" />
          </div>
          <p class="thank-you-heading">Thank you for your feedback</p>
          <div class="thank-you-message-section">
            <p class="thank-you-message">
              We are sorry for the bad experience. We are on it. Expect an update quickly.
            </p>
            <p
              *ngIf="!feedbackOnlyFlow && config?.google_review_url"
              class="thank-you-message-redirect"
            >
              Please
              <a
                [href]="config?.google_review_url"
                (click)="closeThankYou()"
                class="hyper-link-text"
              >click here</a>
              to review us.
            </p>
          </div>
          <div class="col-md-12 col-sm-12 text-center">
            <button class="btn btn-submit" (click)="closeThankYou()">GOT IT</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Powered-by mark ------------------------------------------------- -->
    <div class="powered-by" *ngIf="config.show_powered_by_logo">
      <span>Powered by</span>
      <a
        href="https://www.zceppa.com/?utm_source=zceppa&utm_medium=consumer_link&utm_campaign=review_invite"
        target="_blank"
        rel="noopener"
      >
        <img src="assets/images/zceppadark.svg" alt="Zceppa" />
      </a>
    </div>
  </div>
</div>
